import React, { useEffect, useState, Children } from 'react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Link as LinkIcon, MessageCircle, Heart, Zap, Shield, Users } from 'lucide-react';
// Animation variants
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};
// Dynamic text component
const DynamicText = () => {
  const words = ['games', 'drama', 'ghosting', 'confusion', 'situationships', 'what are we?'];
  const [index, setIndex] = useState(0);
  const [isCrossed, setIsCrossed] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  useEffect(() => {
    const cycleWord = async () => {
      // 1. Show word (already visible)
      // 2. Wait 1.5s
      await new Promise(r => setTimeout(r, 1500));
      // 3. Cross it out
      setIsCrossed(true);
      // 4. Wait 0.8s
      await new Promise(r => setTimeout(r, 800));
      // 5. Fade out
      setIsVisible(false);
      // 6. Wait for fade out (0.3s)
      await new Promise(r => setTimeout(r, 300));
      // 7. Change word, reset states
      setIndex(prev => (prev + 1) % words.length);
      setIsCrossed(false);
      setIsVisible(true);
    };
    cycleWord();
  }, [index]);
  return <span className="relative inline-block min-w-[180px] text-left" data-id="element-510">
      <AnimatePresence mode="wait" data-id="element-511">
        {isVisible && <motion.span key={words[index]} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className={`text-gray-400 relative inline-block transition-all duration-300`} data-id="element-512">
            {words[index]}
            <motion.span className="absolute left-0 top-1/2 w-full h-1 bg-pink-500 rounded-full origin-left" initial={{
          scaleX: 0
        }} animate={{
          scaleX: isCrossed ? 1 : 0
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} data-id="element-513" />
          </motion.span>}
      </AnimatePresence>
    </span>;
};
export function HowItWorks() {
  return <Layout data-id="element-514">
      <div className="bg-white overflow-hidden" data-id="element-515">
        {/* Hero Section */}
        <section className="pt-20 pb-16 md:pt-32 md:pb-24 text-center px-4" data-id="element-516">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="max-w-4xl mx-auto" data-id="element-517">
            <Badge variant="accent" className="mb-6 bg-pink-100 text-pink-600 border-none" data-id="element-518">
              The Anti-Ghosting Protocol 👻🚫
            </Badge>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tighter mb-6" data-id="element-519">
              Dating without the <DynamicText data-id="element-520" />
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto mb-10" data-id="element-521">
              See someone cute on Insta? Don't just like their story. Send a
              FlingLink. 7 days. Clear rules. Zero confusion.
            </p>
            <a href="https://me.flinglink.app/signup" data-id="element-522">
              <Button size="lg" style={{
              backgroundColor: '#ff6b6b'
            }} className="rounded-full px-8 text-lg h-14" data-id="element-523">
                Start Your First Fling <ArrowRight className="ml-2 w-5 h-5" data-id="element-524" />
              </Button>
            </a>
          </motion.div>
        </section>

        {/* Step 1: The Setup */}
        <section className="py-20 md:py-32 bg-gray-50" data-id="element-525">
          <div className="container mx-auto px-4" data-id="element-526">
            <div className="flex flex-col md:flex-row items-center gap-12 md:gap-24" data-id="element-527">
              <motion.div className="flex-1" initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-100px'
            }} variants={fadeInUp} data-id="element-528">
                <div className="w-12 h-12 rounded-full bg-black text-white flex items-center justify-center text-xl font-bold mb-6" data-id="element-529">
                  1
                </div>
                <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-530">
                  Set the Vibe.
                </h2>
                <p className="text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-531">
                  You spot them. You're interested. But you're not trying to
                  marry them (yet). Create a Fling and set the boundaries
                  upfront.
                </p>
                <ul className="space-y-4" data-id="element-532">
                  <li className="flex items-center gap-3 text-lg font-medium" data-id="element-533">
                    <div className="w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center" data-id="element-534">
                      ✨
                    </div>
                    Define the Goal: "See if we click"
                  </li>
                  <li className="flex items-center gap-3 text-lg font-medium" data-id="element-535">
                    <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center" data-id="element-536">
                      🧠
                    </div>
                    Set Boundaries: "No overthinking"
                  </li>
                </ul>
              </motion.div>

              <motion.div className="flex-1 w-full max-w-md" initial={{
              opacity: 0,
              x: 50
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.8
            }} data-id="element-537">
                <div className="bg-white rounded-[2.5rem] p-6 shadow-2xl border border-gray-100 rotate-2 hover:rotate-0 transition-transform duration-500" data-id="element-538">
                  <div className="flex justify-between items-center mb-6" data-id="element-539">
                    <span className="font-bold text-lg" data-id="element-540">New Fling</span>
                    <span className="text-gray-300" data-id="element-541">Step 1/3</span>
                  </div>
                  <div className="space-y-4" data-id="element-542">
                    <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100" data-id="element-543">
                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-2" data-id="element-544">
                        The Vibe
                      </div>
                      <div className="text-xl font-bold" data-id="element-545">
                        Chill & Curious 😌
                      </div>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100" data-id="element-546">
                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-2" data-id="element-547">
                        The Rules
                      </div>
                      <div className="flex flex-wrap gap-2" data-id="element-548">
                        <span className="bg-white px-3 py-1 rounded-full text-sm font-medium shadow-sm" data-id="element-549">
                          Keep it private 🤫
                        </span>
                        <span className="bg-white px-3 py-1 rounded-full text-sm font-medium shadow-sm" data-id="element-550">
                          Text daily 📱
                        </span>
                      </div>
                    </div>
                    <a href="https://me.flinglink.app/signup" className="block mt-4" data-id="element-551">
                      <Button className="w-full" style={{
                      backgroundColor: '#ff6b6b'
                    }} data-id="element-552">
                        Create Link
                      </Button>
                    </a>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Step 2: The Link */}
        <section className="py-20 md:py-32" data-id="element-553">
          <div className="container mx-auto px-4" data-id="element-554">
            <div className="flex flex-col md:flex-row-reverse items-center gap-12 md:gap-24" data-id="element-555">
              <motion.div className="flex-1" initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-100px'
            }} variants={fadeInUp} data-id="element-556">
                <div className="w-12 h-12 rounded-full bg-black text-white flex items-center justify-center text-xl font-bold mb-6" data-id="element-557">
                  2
                </div>
                <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-558">
                  Slide the Link.
                </h2>
                <p className="text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-559">
                  Insta, Snap, WhatsApp, Hinge—doesn't matter. Send them your
                  unique FlingLink. It's low pressure. "Hey, let's try this
                  7-day thing."
                </p>
                <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100 inline-block" data-id="element-560">
                  <div className="flex items-center gap-3 text-gray-500 mb-2 text-sm" data-id="element-561">
                    <MessageCircle size={16} data-id="element-562" /> Instagram DM
                  </div>
                  <div className="bg-blue-500 text-white p-4 rounded-2xl rounded-tr-none max-w-xs" data-id="element-563">
                    Saw you on FlingLink. The vibe check is immaculate. You in?
                    👀
                    <div className="mt-2 bg-white/10 p-2 rounded-lg flex items-center gap-2" data-id="element-564">
                      <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center text-black font-bold" data-id="element-565">
                        FL
                      </div>
                      <span className="text-sm underline decoration-white/50" data-id="element-566">
                        flingl.ink/u/alex
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div className="flex-1 w-full max-w-md flex justify-center" initial={{
              opacity: 0,
              scale: 0.8
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              type: 'spring',
              bounce: 0.4
            }} data-id="element-567">
                <div className="relative" data-id="element-568">
                  <div className="absolute inset-0 bg-gradient-to-r from-pink-400 to-purple-500 rounded-full blur-3xl opacity-20 animate-pulse" data-id="element-569"></div>
                  <div className="relative bg-white p-8 rounded-[3rem] shadow-2xl border-4 border-gray-50 text-center" data-id="element-570">
                    <LinkIcon className="w-16 h-16 mx-auto mb-4 text-gray-900" data-id="element-571" />
                    <div className="text-2xl font-bold mb-2" data-id="element-572">
                      flingl.ink/u/you
                    </div>
                    <div className="text-gray-500 mb-6" data-id="element-573">Ready to send</div>
                    <Button variant="outline" className="rounded-full" data-id="element-574">
                      Copy Link 🔗
                    </Button>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Step 3: The Ritual */}
        <section className="py-20 md:py-32 bg-black text-white" data-id="element-575">
          <div className="container mx-auto px-4" data-id="element-576">
            <div className="flex flex-col md:flex-row items-center gap-12 md:gap-24" data-id="element-577">
              <motion.div className="flex-1" initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-100px'
            }} variants={fadeInUp} data-id="element-578">
                <div className="w-12 h-12 rounded-full bg-white text-black flex items-center justify-center text-xl font-bold mb-6" data-id="element-579">
                  3
                </div>
                <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-580">
                  The 7-Day Ritual.
                </h2>
                <p className="text-xl text-gray-400 mb-8 leading-relaxed" data-id="element-581">
                  Chat in FlingLink or keep it on your socials (Insta, WhatsApp,
                  wherever). FlingLink just adds the daily Spark and Pulse
                  Check. 2 minutes a day. That's it.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-582">
                  <div className="bg-gray-900 p-5 rounded-xl border border-gray-800" data-id="element-583">
                    <Zap className="text-yellow-400 mb-3" data-id="element-584" />
                    <h3 className="font-bold text-lg mb-1" data-id="element-585">Daily Spark</h3>
                    <p className="text-gray-400 text-sm" data-id="element-586">
                      One fun prompt/challenge per day to break the ice.
                    </p>
                  </div>
                  <div className="bg-gray-900 p-5 rounded-xl border border-gray-800" data-id="element-587">
                    <Heart className="text-pink-500 mb-3" data-id="element-588" />
                    <h3 className="font-bold text-lg mb-1" data-id="element-589">Pulse Check</h3>
                    <p className="text-gray-400 text-sm" data-id="element-590">
                      Privately signal how you're feeling. No awkward talks.
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div className="flex-1 w-full max-w-md" initial={{
              opacity: 0,
              y: 50
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} data-id="element-591">
                <div className="bg-gray-900 rounded-[2.5rem] p-6 border border-gray-800 relative overflow-hidden" data-id="element-592">
                  <div className="absolute top-0 right-0 p-4" data-id="element-593">
                    <div className="w-12 h-12 rounded-full border-4 border-gray-800 flex items-center justify-center text-lg font-bold text-white relative" data-id="element-594">
                      7
                      <svg className="absolute inset-0 w-full h-full -rotate-90" data-id="element-595">
                        <circle cx="24" cy="24" r="20" stroke="#374151" strokeWidth="4" fill="none" data-id="element-596" />
                        <circle cx="24" cy="24" r="20" stroke="#ff6b6b" strokeWidth="4" fill="none" strokeDasharray="126" strokeDashoffset="40" data-id="element-597" />
                      </svg>
                    </div>
                  </div>
                  <div className="mt-8 mb-8" data-id="element-598">
                    <div className="text-sm text-yellow-500 font-bold mb-2 uppercase tracking-wider" data-id="element-599">
                      Day 3 Spark ✨
                    </div>
                    <div className="text-2xl font-bold leading-tight" data-id="element-600">
                      "Send a voice note describing your perfect Sunday
                      morning."
                    </div>
                  </div>
                  <div className="bg-gray-800 rounded-xl p-4 flex items-center gap-3" data-id="element-601">
                    <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center" data-id="element-602">
                      ▶️
                    </div>
                    <div className="h-1 bg-gray-700 flex-1 rounded-full overflow-hidden" data-id="element-603">
                      <div className="w-1/3 h-full bg-white" data-id="element-604"></div>
                    </div>
                    <div className="text-xs text-gray-400" data-id="element-605">0:14</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Step 4: The Decision */}
        <section className="py-20 md:py-32 bg-pink-50" data-id="element-606">
          <div className="container mx-auto px-4 text-center" data-id="element-607">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} className="max-w-3xl mx-auto mb-16" data-id="element-608">
              <div className="w-12 h-12 rounded-full bg-black text-white flex items-center justify-center text-xl font-bold mb-6 mx-auto" data-id="element-609">
                4
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-610">
                Day 7: The Moment.
              </h2>
              <p className="text-xl text-gray-600" data-id="element-611">
                No ghosting. No "what are we?". Just pick a card and we handle
                the rest.
              </p>
            </motion.div>

            <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} data-id="element-612">
              {[{
              title: 'Renew',
              icon: '🔄',
              desc: 'Another 7 days?',
              color: 'bg-blue-100 text-blue-600'
            }, {
              title: 'Graduate',
              icon: '💘',
              desc: 'Make it official.',
              color: 'bg-pink-100 text-pink-600'
            }, {
              title: 'Friendly Landing',
              icon: '🤝',
              desc: 'End it kindly.',
              color: 'bg-gray-200 text-gray-600'
            }].map((card, i) => <motion.div key={i} variants={fadeInUp} data-id="element-613">
                  <Card className="p-8 h-full flex flex-col items-center justify-center hover:shadow-xl transition-shadow cursor-default" data-id="element-614">
                    <div className={`w-16 h-16 rounded-full ${card.color} flex items-center justify-center text-3xl mb-4`} data-id="element-615">
                      {card.icon}
                    </div>
                    <h3 className="text-xl font-bold mb-2" data-id="element-616">{card.title}</h3>
                    <p className="text-gray-500" data-id="element-617">{card.desc}</p>
                  </Card>
                </motion.div>)}
            </motion.div>
          </div>
        </section>

        {/* Connector Section */}
        <section className="py-20 border-t" data-id="element-618">
          <div className="container mx-auto px-4" data-id="element-619">
            <div className="bg-black text-white rounded-[3rem] p-8 md:p-16 text-center relative overflow-hidden" data-id="element-620">
              <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10" data-id="element-621"></div>
              <div className="relative z-10 max-w-2xl mx-auto" data-id="element-622">
                <Users className="w-12 h-12 mx-auto mb-6 text-yellow-400" data-id="element-623" />
                <h2 className="text-3xl md:text-5xl font-bold mb-6" data-id="element-624">
                  Need an Intro?
                </h2>
                <p className="text-lg md:text-xl text-gray-300" data-id="element-625">
                  Know someone who knows them? Ask your mutual friend to be a
                  Connector. They can set up the FlingLink for you, so you don't
                  have to slide into the DMs cold.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ / Philosophy */}
        <section className="py-20 md:py-32" data-id="element-626">
          <div className="container mx-auto px-4 max-w-3xl" data-id="element-627">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-628">
              Why we built this.
            </h2>

            <div className="space-y-8" data-id="element-629">
              <div className="flex gap-4" data-id="element-630">
                <div className="w-8 h-8 rounded-full bg-pink-100 text-pink-500 flex items-center justify-center flex-shrink-0 font-bold" data-id="element-631">
                  ?
                </div>
                <div data-id="element-632">
                  <h3 className="text-xl font-bold mb-2" data-id="element-633">
                    Is this a dating app?
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-634">
                    Nope. No profiles to swipe on. No discovery feed. FlingLink
                    is a tool for people you
                    <span className="italic" data-id="element-635"> already know</span> or have seen
                    on socials. It's a container for that fragile early stage.
                  </p>
                </div>
              </div>

              <div className="flex gap-4" data-id="element-636">
                <div className="w-8 h-8 rounded-full bg-pink-100 text-pink-500 flex items-center justify-center flex-shrink-0 font-bold" data-id="element-637">
                  ?
                </div>
                <div data-id="element-638">
                  <h3 className="text-xl font-bold mb-2" data-id="element-639">Where do we chat?</h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-640">
                    Your choice. Chat in FlingLink or keep it on your socials
                    (Insta, WhatsApp, Snap—wherever). FlingLink just adds the
                    structure (Sparks) and the safety net (Pulse Checks). Total
                    flexibility.
                  </p>
                </div>
              </div>

              <div className="flex gap-4" data-id="element-641">
                <div className="w-8 h-8 rounded-full bg-pink-100 text-pink-500 flex items-center justify-center flex-shrink-0 font-bold" data-id="element-642">
                  ?
                </div>
                <div data-id="element-643">
                  <h3 className="text-xl font-bold mb-2" data-id="element-644">
                    What happens if they say no?
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-645">
                    Then you know! And you didn't have to have a 3-hour "what
                    are we" conversation. Friendly Landing means you both walk
                    away with dignity.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-24 text-center" data-id="element-646">
          <div className="container mx-auto px-4" data-id="element-647">
            <h2 className="text-4xl md:text-6xl font-bold mb-8 tracking-tighter" data-id="element-648">
              Ready to stop guessing?
            </h2>
            <a href="https://me.flinglink.app/signup" data-id="element-649">
              <Button size="lg" style={{
              backgroundColor: '#ff6b6b'
            }} className="rounded-full px-12 py-8 text-xl h-auto shadow-xl hover:scale-105 transition-transform" data-id="element-650">
                Get Your FlingLink
              </Button>
            </a>
          </div>
        </section>
      </div>
    </Layout>;
}